import type { ChatMessage, CreationStatus } from '@/types/Dashboards.types'

/**
 * Derives the current dashboard creation step from session state.
 * Per brand guide Section 12.
 *
 * 5-step mode (default — used at /dashboards/:id):
 * | Condition | Step |
 * |---|---|
 * | No messages yet | 1 |
 * | User has submitted a dashboard type choice (1 user msg) | 2 |
 * | User has submitted a data source choice (2+ user msgs) | 3 |
 * | creationStatus === 'fetching_data' | 4 |
 * | last AI message type is 'panel_proposals' OR creationStatus === 'awaiting_confirmation' | 5 |
 *
 * 4-step mode (used at /dashboards/new full-screen creation):
 * | Condition | Step |
 * |---|---|
 * | No messages yet | 1 |
 * | 1 user message | 2 |
 * | 2+ user messages (pre-filter confirm) | 3 |
 * | creationStatus === 'fetching_data' | 4 |
 * | creationStatus === 'awaiting_confirmation' OR last AI msg is panel_proposals | 4 |
 */
export function deriveCreationStep(
  messages: ChatMessage[],
  creationStatus: CreationStatus | null,
  totalSteps = 5,
): number {
  if (totalSteps === 4) {
    // 4-step mode: fetching_data and awaiting_confirmation both map to step 4
    if (creationStatus === 'fetching_data') return 4
    if (creationStatus === 'awaiting_confirmation') return 4

    const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant')
    if (lastAssistantMsg?.type === 'panel_proposals') return 4

    const userMessages = messages.filter(m => m.role === 'user')
    if (userMessages.length >= 2) return 3
    if (userMessages.length === 1) return 2
    return 1
  }

  // Default: 5-step mode
  if (creationStatus === 'fetching_data') return 4
  if (creationStatus === 'awaiting_confirmation') return 5

  const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant')
  if (lastAssistantMsg?.type === 'panel_proposals') return 5

  const userMessages = messages.filter(m => m.role === 'user')
  if (userMessages.length >= 2) return 3
  if (userMessages.length === 1) return 2

  return 1
}
